import BrowserBroadcastChannelService from './BrowserBroadcastChannelService.js'

class NotificationCoordinatorService {
  constructor() {
    this.broadcastService = BrowserBroadcastChannelService.getInstance()
    this.pendingRequestResolvers = new Map()

    // Subscribe to notification-related messages
    this.broadcastService.subscribe(
      'REQUEST_VISIBLE_TAB',
      this.handleVisibleTabRequest.bind(this)
    )
    this.broadcastService.subscribe(
      'VISIBLE_TAB_RESPONSE',
      this.handleVisibleTabResponse.bind(this)
    )
    this.broadcastService.subscribe(
      'NOTIFICATION_SHOWN',
      this.handleNotificationShown.bind(this)
    )
  }

  /**
   * Determine if and how notification should be shown
   * @returns {Promise<{showToast: boolean, showDesktop: boolean}>}
   */
  async shouldShowNotification() {
    if (
      this.broadcastService.useBroadcastChannel &&
      !this.broadcastService.isChannelClosed
    ) {
      return await this.checkWithBroadcastChannel()
    } else {
      return this.checkWithLocalStorage()
    }
  }

  /**
   * Check notification decision using BroadcastChannel
   * @returns {Promise<{showToast: boolean, showDesktop: boolean}>}
   */
  async checkWithBroadcastChannel() {
    // If no channel or closed, show toast as fallback
    if (
      !this.broadcastService.channel ||
      this.broadcastService.isChannelClosed
    ) {
      return { showToast: true, showDesktop: false }
    }

    // If current tab is visible, check if other visible tabs exist
    if (!document.hidden) {
      const requestId = this.broadcastService.generateTabId()

      return new Promise(resolve => {
        const timeout = setTimeout(() => {
          this.pendingRequestResolvers.delete(requestId)
          // No other visible tabs responded, show toast here
          resolve({ showToast: true, showDesktop: false })
        }, 150)

        this.pendingRequestResolvers.set(requestId, result => {
          clearTimeout(timeout)
          resolve(result)
        })

        // Ask if any other tabs are visible
        this.broadcastService.broadcast('REQUEST_VISIBLE_TAB', { requestId })
      })
    }

    // Tab is hidden, check if we're leader and have permission for desktop notifications
    if (this.broadcastService.isLeader) {
      const hasNotificationPermission =
        'Notification' in window && Notification.permission === 'granted'

      if (hasNotificationPermission) {
        return { showToast: false, showDesktop: true }
      }
    }

    // Hidden tab, not leader or no permission - don't show notification
    return { showToast: false, showDesktop: false }
  }

  /**
   * Check notification decision using localStorage fallback
   * @returns {{showToast: boolean, showDesktop: boolean}}
   */
  checkWithLocalStorage() {
    const now = Date.now()
    const storageKey = this.broadcastService.storageKey + '_notifications'
    const leaderInfo = JSON.parse(localStorage.getItem(storageKey) || '{}')

    // If current tab is visible, show toast and mark as visible leader
    if (!document.hidden) {
      localStorage.setItem(
        storageKey,
        JSON.stringify({
          tabId: this.broadcastService.tabId,
          timestamp: now,
          isVisible: true
        })
      )
      return { showToast: true, showDesktop: false }
    }

    // Check if there's a recent visible leader
    const isRecentLeader =
      leaderInfo.tabId &&
      leaderInfo.isVisible &&
      now - leaderInfo.timestamp < 2000

    if (isRecentLeader) {
      // Another visible tab is handling notifications
      return { showToast: false, showDesktop: false }
    }

    // No recent visible leader, this hidden tab should show desktop notification
    localStorage.setItem(
      storageKey,
      JSON.stringify({
        tabId: this.broadcastService.tabId,
        timestamp: now,
        isVisible: false
      })
    )

    return { showToast: false, showDesktop: true }
  }

  /**
   * Handle request from other tab asking if any tab is visible
   */
  handleVisibleTabRequest(data) {
    if (!document.hidden) {
      this.broadcastService.broadcast('VISIBLE_TAB_RESPONSE', {
        tabId: this.broadcastService.tabId,
        requestId: data.requestId,
        isLeader: this.broadcastService.isLeader
      })
    }
  }

  /**
   * Handle response from visible tab
   */
  handleVisibleTabResponse(data) {
    const resolver = this.pendingRequestResolvers.get(data.requestId)
    if (resolver && data.tabId !== this.broadcastService.tabId) {
      // Another visible tab exists, don't show notification here
      resolver({ showToast: false, showDesktop: false })
      this.pendingRequestResolvers.delete(data.requestId)
    }
  }

  /**
   * Handle notification shown event from other tabs
   */
  handleNotificationShown(data) {
    // Could be used for analytics or coordination logic
    console.debug('Notification shown in tab:', data.tabId)
  }

  /**
   * Mark that notification was shown in this tab
   */
  markNotificationShown() {
    this.broadcastService.broadcast('NOTIFICATION_SHOWN', {
      tabId: this.broadcastService.tabId,
      timestamp: Date.now()
    })
  }

  /**
   * Legacy method for backwards compatibility
   */
  notificationShown() {
    this.markNotificationShown()
  }

  /**
   * Check if desktop notifications are supported and permitted
   */
  hasDesktopNotificationPermission() {
    return 'Notification' in window && Notification.permission === 'granted'
  }

  /**
   * Request desktop notification permission
   */
  async requestDesktopNotificationPermission() {
    if (!('Notification' in window)) {
      return 'not-supported'
    }

    if (Notification.permission === 'granted') {
      return 'granted'
    }

    if (Notification.permission === 'denied') {
      return 'denied'
    }

    try {
      const permission = await Notification.requestPermission()
      return permission
    } catch (error) {
      console.error('Error requesting notification permission:', error)
      return 'denied'
    }
  }

  /**
   * Clean up resources
   */
  destroy() {
    this.pendingRequestResolvers.clear()
    // Unsubscribe from broadcast service
    this.broadcastService.unsubscribe(
      'REQUEST_VISIBLE_TAB',
      this.handleVisibleTabRequest
    )
    this.broadcastService.unsubscribe(
      'VISIBLE_TAB_RESPONSE',
      this.handleVisibleTabResponse
    )
    this.broadcastService.unsubscribe(
      'NOTIFICATION_SHOWN',
      this.handleNotificationShown
    )
  }

  /**
   * Get singleton instance
   */
  static getInstance() {
    if (!NotificationCoordinatorService.instance) {
      NotificationCoordinatorService.instance = new NotificationCoordinatorService()
    }
    return NotificationCoordinatorService.instance
  }
}

export default NotificationCoordinatorService
